import React, { useCallback, useState, useEffect, useLayoutEffect, useMemo } from 'react';
import Animated from 'react-native-reanimated';
import { StyleSheet } from 'react-native';
import Indicator from 'design-system/Indicator';
import { HEIGHT_PLUS_PADDING } from 'design-system/Button';
import IconFilterBold from 'design-system/icons/IconFilterBold';
import { rem, SCROLL_INDICATOR_INSETS } from 'design-system/values';
import { AppView, Spacer, withAnimatedNavigationHeader } from 'design-system';
import ReanimatedStackHeader from 'design-system/hoc/ReanimatedStackHeader';
import NavigationHeaderRightIconButton from 'design-system/NavigationHeaderRightIconButton';
import useColors from 'hooks/useColors';
import { useAppSelector } from 'store/hooks';
import { useMarginBottom } from 'hooks/useMarginBottom';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import TrendingStock from '../components/TrendingStock';
import Header from '../components/CategoryHeaderComponent';
import FundPickerFloatingBar from '../components/FundPickerFloatingBar';
import useStaticCollections from '../hooks/useStaticCollections';
import useFundPickerStore from '../hooks/useFundPickerStore';
import { AssetFilterCategory } from '../types';
import { FilterAssetsModal } from '../components/FilterAssetsModal';
import SetupInvestmentAccountButton from '../components/SetupInvestmentAccountButton';
import { selectIsTradingAccountAlpaca, selectShowSetupInvestAccountInInvestScreens } from '../reducers/selectors';
import { sortAssetIdAndSymbolByPrice, sortAssetIdAndSymbolsByName, sortAssetIdAndSymbolByPriceChange, sortAssetIdAndSymbolsBySymbol, } from '../utils/assets';
import { isWeb } from '../../../constants';
const checkHasFilter = (id) => id === 'BIGGEST_GAINERS' || id === 'BIGGEST_LOSERS';
const HeaderRight = ({ id, openFilterModal, }) => {
    const colors = useColors();
    const hasFilter = checkHasFilter(id);
    if (!hasFilter)
        return null;
    const onFilterPress = () => {
        openFilterModal?.();
    };
    return (<NavigationHeaderRightIconButton onPress={onFilterPress}>
      <IconFilterBold color={colors.text.primary}/>
    </NavigationHeaderRightIconButton>);
};
const StaticCollectionDetails = withAnimatedNavigationHeader(({ route, scrollY, navigation, scrollHandler }) => {
    const id = route.params?.id;
    const Icon = route.params?.Icon;
    const name = route.params?.title;
    const description = route.params?.description;
    const depositAndBuyFlowParams = route.params?.depositAndBuyFlowParams;
    const isActiveTradingAccountAlpaca = useAppSelector(selectIsTradingAccountAlpaca);
    const hasFilter = checkHasFilter(id);
    const showSetupInvestAccountInInvestScreens = useAppSelector(selectShowSetupInvestAccountInInvestScreens);
    const { active: fundPickerMode } = useFundPickerStore();
    const [state, setState] = useState({
        selectedFilter: AssetFilterCategory.PRICE_CHANGE,
    });
    useLayoutEffect(() => {
        const headerTitleWidth = rem(150);
        navigation.setOptions({
            ...(isActiveTradingAccountAlpaca
                ? {
                    headerTitle: () => (<ReanimatedStackHeader title={route.params?.title} scrollY={scrollY} width={headerTitleWidth}/>),
                }
                : {
                    title: 'Most popular',
                }),
        });
    }, [isActiveTradingAccountAlpaca, route.params?.title, scrollY]);
    useLayoutEffect(() => {
        if (hasFilter) {
            const onSelectFilter = (filter) => {
                setState((oldState) => ({
                    ...oldState,
                    selectedFilter: filter,
                }));
            };
            const openFilterModal = () => {
                FilterAssetsModal.show(state.selectedFilter, onSelectFilter);
            };
            navigation.setOptions({
                headerRight: () => <HeaderRight id={id} openFilterModal={openFilterModal}/>,
            });
        }
        else {
            navigation.setOptions({
                headerRight: () => null,
            });
        }
    }, [hasFilter, id, state.selectedFilter]);
    const { fetching, list } = useStaticCollections(id);
    const listIndexLength = useMemo(() => (list?.length || 0) - 1, [list.length]);
    const stockMetaCache = useAppSelector((state) => state.invest.caches);
    const priceChangeCache = useAppSelector((state) => state.invest.priceChangeCaches);
    const currentPriceCache = useAppSelector((state) => state.invest.currentPriceCaches);
    const [displayList, setDisplayList] = useState([]);
    useEffect(() => {
        if (hasFilter) {
            switch (state.selectedFilter) {
                case AssetFilterCategory.PRICE:
                    setDisplayList(sortAssetIdAndSymbolByPrice(currentPriceCache, list));
                    break;
                case AssetFilterCategory.PRICE_CHANGE:
                    // They are already sorted. Kept this condition because in future we might miss this condition.
                    if (id === 'BIGGEST_GAINERS' || id === 'BIGGEST_LOSERS') {
                        setDisplayList(list);
                        break;
                    }
                    setDisplayList(sortAssetIdAndSymbolByPriceChange(priceChangeCache, list));
                    break;
                case AssetFilterCategory.NAME:
                    setDisplayList(sortAssetIdAndSymbolsByName(stockMetaCache, list));
                    break;
                case AssetFilterCategory.SYMBOL: {
                    setDisplayList(sortAssetIdAndSymbolsBySymbol(list));
                    break;
                }
                default:
                    break;
            }
        }
        else {
            setDisplayList(list);
        }
    }, [state.selectedFilter, list, currentPriceCache, stockMetaCache, id, priceChangeCache, hasFilter]);
    const renderItem = useCallback(({ item, index }) => (<TrendingStock assetId={item.assetId} isFirstItem={index === 0} isLastItem={index === listIndexLength}/>), [listIndexLength]);
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const hasFloatingButton = (showSetupInvestAccountInInvestScreens && !depositAndBuyFlowParams) || fundPickerMode;
    const contentContainerStyle = useMarginBottom('paddingBottom', hasFloatingButton ? HEIGHT_PLUS_PADDING : 0, paddingHorizontalStyle);
    return (<AppView withFrame={false}>
        <Animated.FlatList data={displayList} renderItem={renderItem} initialNumToRender={20} onScroll={scrollHandler} scrollEventThrottle={1} keyboardDismissMode="on-drag" showsVerticalScrollIndicator contentContainerStyle={contentContainerStyle} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS} keyExtractor={(item) => item.assetId} ListHeaderComponent={isActiveTradingAccountAlpaca ? (<Header name={name} Icon={Icon} description={description} total={displayList.length}/>) : (<Spacer h={16}/>)}/>
        {fetching && !displayList.length ? <Indicator dark style={styles.absView}/> : null}
        {showSetupInvestAccountInInvestScreens && !depositAndBuyFlowParams ? (<SetupInvestmentAccountButton brand floating/>) : null}
        {fundPickerMode ? <FundPickerFloatingBar popCount={2}/> : null}
      </AppView>);
}, ({ colors }) => ({
    hasCustomTitle: true,
    headerColorOutputRange: [colors.background.dark, colors.cards.onDark],
}));
const styles = StyleSheet.create({
    absView: {
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        position: 'absolute',
    },
});
export default StaticCollectionDetails;
